import { useCallback, useEffect, useRef, useState } from 'react';

export interface PageMenuSection {
    key: string;
    label: string;
}

/** Default top offset to clear the fixed header (px). */
const DEFAULT_SCROLL_OFFSET = 180;

interface UsePageMenuScrollOptions {
    sections: PageMenuSection[];
    /** Pixel threshold from the top of the viewport to consider a section "active" (default: 150) */
    scrollThreshold?: number;
    /** Top offset subtracted when scrolling to a section, to clear fixed headers (default: 80) */
    scrollOffset?: number;
    /** Prefix for DOM id-based lookup when no ref is registered (default: "section-") */
    idPrefix?: string;
}

/** Resolve the DOM element for a given section key: ref first, then id lookup. */
const resolveElement = (refs: Record<string, HTMLElement | null>, key: string, idPrefix: string): HTMLElement | null => {
    return refs[key] ?? document.getElementById(`${idPrefix}${key}`);
};

/**
 * Hook that manages scroll-based section tracking and smooth scroll on click.
 *
 * When the user clicks a menu item the hook scrolls to that section and locks
 * the active state so that the passive scroll listener does not fight back.
 * The lock is released via the `scrollend` event once the browser finishes scrolling.
 *
 * Sections can be targeted either by registering a ref via `setSectionRef` or
 * by placing a DOM element with `id="${idPrefix}${key}"` (default prefix: `"section-"`).
 */
export const usePageMenuScroll = ({
    sections,
    scrollThreshold = 250,
    scrollOffset = DEFAULT_SCROLL_OFFSET,
    idPrefix = 'section-'
}: UsePageMenuScrollOptions) => {
    const [activeSection, setActiveSection] = useState(sections[0]?.key ?? '');
    const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
    const isClickScrolling = useRef(false);

    const handleSectionClick = useCallback(
        (sectionKey: string) => {
            isClickScrolling.current = true;
            setActiveSection(sectionKey);

            const el = resolveElement(sectionRefs.current, sectionKey, idPrefix);
            if (el) {
                const top = el.getBoundingClientRect().top + window.scrollY - scrollOffset;
                window.scrollTo({ top, behavior: 'smooth' });
            }

            // Release the lock when the browser confirms scrolling has stopped.
            window.addEventListener(
                'scrollend',
                () => {
                    isClickScrolling.current = false;
                },
                { once: true }
            );
        },
        [idPrefix, scrollOffset]
    );

    useEffect(() => {
        const keys = sections.map((s) => s.key);
        let ticking = false;

        const handleScroll = () => {
            if (isClickScrolling.current) return;
            if (ticking) return;
            ticking = true;

            requestAnimationFrame(() => {
                let currentSection = keys[0];

                for (const key of keys) {
                    const el = resolveElement(sectionRefs.current, key, idPrefix);
                    if (el) {
                        const rect = el.getBoundingClientRect();
                        if (rect.top <= scrollThreshold) {
                            currentSection = key;
                        }
                    }
                }

                setActiveSection(currentSection);
                ticking = false;
            });
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [sections, scrollThreshold, idPrefix]);

    /** Callback ref – use as `ref={(el) => setSectionRef('key', el)}` */
    const setSectionRef = useCallback((key: string, el: HTMLElement | null) => {
        sectionRefs.current[key] = el;
    }, []);

    return { activeSection, handleSectionClick, setSectionRef, sectionRefs };
};
